import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Send, Plus, Search, Play, Pause, Square, BarChart3, Eye } from 'lucide-react'
import {
  useWaCampaigns,
  useStartWaCampaign,
  usePauseWaCampaign,
  useCancelWaCampaign,
} from '@hooks/useWaCampaigns'
import { WaCampaign, WaCampaignStatus } from '@/types'
import { cn } from '@lib/utils'
import { CreateWaCampaignDialog } from '@components/whatsapp/CreateWaCampaignDialog'

const statusColors: Record<string, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800' },
  SCHEDULED: { bg: 'bg-blue-100', text: 'text-blue-800' },
  RUNNING: { bg: 'bg-green-100', text: 'text-green-800' },
  PAUSED: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  COMPLETED: { bg: 'bg-purple-100', text: 'text-purple-800' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-800' },
}

const defaultStatusColor = { bg: 'bg-gray-100', text: 'text-gray-800' }

const getStatusColor = (status: string) => {
  return statusColors[status?.toUpperCase()] || defaultStatusColor
}

export function WaCampaignsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<WaCampaignStatus | ''>('')
  const [page, setPage] = useState(1)
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  const { data, isLoading, error } = useWaCampaigns({
    search,
    status: statusFilter || undefined,
    page,
    limit: 20,
  })

  const startCampaign = useStartWaCampaign()
  const pauseCampaign = usePauseWaCampaign()
  const cancelCampaign = useCancelWaCampaign()

  const handleStart = async (id: number) => {
    await startCampaign.mutateAsync(id)
  }

  const handlePause = async (id: number) => {
    await pauseCampaign.mutateAsync(id)
  }

  const handleCancel = async (id: number) => {
    if (confirm('Are you sure you want to cancel this campaign?')) {
      await cancelCampaign.mutateAsync(id)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Send size={32} className="text-green-600" />
          <h1 className="text-3xl font-bold text-gray-900">WhatsApp Campaigns</h1>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 font-medium text-white hover:bg-green-700"
        >
          <Plus size={20} />
          New Campaign
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as WaCampaignStatus | '')
            setPage(1)
          }}
          className="rounded-lg border border-gray-300 py-2.5 px-4 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
        >
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="SCHEDULED">Scheduled</option>
          <option value="RUNNING">Running</option>
          <option value="PAUSED">Paused</option>
          <option value="COMPLETED">Completed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      </div>

      {/* Campaigns Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">
          Error loading campaigns. Please try again.
        </div>
      ) : !data?.data?.length ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-gray-200">
          <Send size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No campaigns found. Create your first WhatsApp campaign!</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.data.map((campaign: WaCampaign) => (
            <div
              key={campaign.id}
              className="rounded-lg border border-gray-200 bg-white p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3
                    onClick={() => navigate(`/whatsapp/campaigns/${campaign.id}`)}
                    className="font-semibold text-gray-900 hover:text-green-600 cursor-pointer"
                  >
                    {campaign.name}
                  </h3>
                  {campaign.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {campaign.description}
                    </p>
                  )}
                </div>
                <span
                  className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    getStatusColor(campaign.status).bg,
                    getStatusColor(campaign.status).text
                  )}
                >
                  {campaign.status}
                </span>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-xs text-gray-500">Recipients</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {campaign.totalRecipients || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Sent</div>
                  <div className="text-lg font-semibold text-green-600">
                    {campaign.sentCount || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Delivered</div>
                  <div className="text-lg font-semibold text-blue-600">
                    {campaign.deliveredCount || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Failed</div>
                  <div className="text-lg font-semibold text-red-600">
                    {campaign.failedCount || 0}
                  </div>
                </div>
              </div>

              {/* Schedule */}
              {campaign.scheduledAt && (
                <div className="text-sm text-gray-500 mb-4">
                  Scheduled: {new Date(campaign.scheduledAt).toLocaleString()}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                {campaign.status === 'DRAFT' && (
                  <button
                    onClick={() => handleStart(campaign.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200"
                  >
                    <Play size={14} />
                    Start
                  </button>
                )}
                {campaign.status === 'RUNNING' && (
                  <button
                    onClick={() => handlePause(campaign.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200"
                  >
                    <Pause size={14} />
                    Pause
                  </button>
                )}
                {campaign.status === 'PAUSED' && (
                  <button
                    onClick={() => handleStart(campaign.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200"
                  >
                    <Play size={14} />
                    Resume
                  </button>
                )}
                {['DRAFT', 'SCHEDULED', 'RUNNING', 'PAUSED'].includes(campaign.status) && (
                  <button
                    onClick={() => handleCancel(campaign.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200"
                  >
                    <Square size={14} />
                    Cancel
                  </button>
                )}
                <button
                  onClick={() => navigate(`/whatsapp/campaigns/${campaign.id}`)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 ml-auto"
                >
                  <Eye size={14} />
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {page} of {data.pagination.totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
              disabled={page === data.pagination.totalPages}
              className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Campaign Dialog */}
      <CreateWaCampaignDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={() => setPage(1)}
      />
    </div>
  )
}
